<template>
    <div>
        <Button borderOnly :class="['inventory-move-modal']" @click="moveInventory" noMargin>
            <i class="fas fa-arrow-right-arrow-left" /> Move
        </Button>
        <div>
            <CustomModal
                v-if="variant && showModal"
                :title="`Moving ${variant.name}`"
                :show="showModal"
                @close="showModal = false"
            >
                <div class="my-3 inventory-move-modal-inner">
                    <div v-if="loading">Loading...</div>
                    <div v-else>
                        <div>
                            <SelectDropdown
                                v-if="warehouseOptions.length"
                                label="Location*"
                                :options="warehouseOptions"
                                :multiple="false"
                                :value="warehouseId"
                                @change="warehouseId = $event"
                            />
                        </div>
                        <div v-if="warehouseId">
                            <div class="row">
                                <div class="col-md-6">
                                    <div>
                                        <SelectDropdown
                                            label="From*"
                                            :options="Object.values(warehouseRooms)"
                                            :multiple="false"
                                            :value="fromWarehouseRoom"
                                            @change="fromWarehouseRoom = $event"
                                        />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div>
                                        <p class="mb-2">To*</p>
                                        <div>
                                            {{
                                                toWarehouseRoom == warehouseRooms.FRONT_ROOM.id
                                                    ? "Front Room"
                                                    : "Back Room"
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Multi-Package Selection -->
                            <div class="package-moves-container">
                                <div 
                                    v-for="(packageMove, index) in packageMoves" 
                                    :key="index"
                                    class="package-move-row"
                                >
                                    <div class="row align-items-end">
                                        <div class="col-md-5">
                                            <SelectDropdown
                                                v-if="packages.length"
                                                :label="`Package ${index + 1}*`"
                                                :options="availablePackagesForRow(index)"
                                                :multiple="false"
                                                :value="packageMove.packageVal"
                                                @change="updatePackage(index, 'packageVal', $event)"
                                            />
                                        </div>
                                        <div class="col-md-5">
                                            <CustomInput
                                                :value="packageMove.quantity"
                                                @input="updatePackage(index, 'quantity', $event)"
                                                placeholder="Quantity"
                                                type="number"
                                                :label="`Qty max(${getPackageMaxQty(packageMove.packageVal)})`"
                                            />
                                        </div>
                                        <div class="col-md-2">
                                            <Button 
                                                v-if="packageMoves.length > 1"
                                                @click="removePackage(index)"
                                                borderOnly
                                                class="remove-package-btn"
                                                :noMargin="true"
                                            >
                                                <i class="fas fa-times"></i>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- ADD PACKAGE Button -->
                                <div class="add-package-section" v-if="canAddMorePackages">
                                    <Button 
                                        @click="addPackage"
                                        borderOnly
                                        class="add-package-btn"
                                        :noMargin="true"
                                    >
                                        <i class="fas fa-plus"></i>
                                        ADD PACKAGE
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="d-flex align-items-center justify-content-between">
                                <div class="unit-to-move">{{ totalUnitsToMove }} Units will be moved.</div>
                                <div class="d-flex" style="gap: 10px">
                                    <Button @click="showModal = false">Cancel</Button>
                                    <Button @click="moveStock">
                                        <i class="fas fa-arrow-right-arrow-left"></i>
                                        {{ movingStock ? "Please wait..." : "Move" }}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div v-if="errorMessage" class="err-msg">{{ errorMessage }}</div>
                    </div>
                </div>
            </CustomModal>
        </div>
    </div>
</template>

<script>
import CustomModal from "../layout/CustomModal.vue"
import { mapState } from "vuex"
import Button from "../UI/Button.vue"
import SelectDropdown from "../crud/SelectDropdown.vue"
import CustomInput from "../crud/custom-input.vue"
import { http } from "../../utils/http"
import { errMsg } from "../../utils/common"
import { getBatchNos, getLedgerEntry, getWarehouseRoomMaxQty } from "../../utils/inventory"

export default {
    emits: ["update"],
    data() {
        return {
            showModal: false,
            loading: false,
            warehouseOptions: [],
            warehouseEntries: [],
            // Legacy fields (keeping for compatibility)
            quantity: 0,
            packageVal: null,
            // New multi-package structure
            packageMoves: [
                {
                    packageVal: null,
                    quantity: 0
                }
            ],
            fromWarehouseRoom: null,
            toWarehouseRoom: null,
            errorMessage: "",
            result: null,
            warehouseId: "",
            movingStock: false,
        }
    },
    components: { CustomInput, CustomModal, Button, SelectDropdown },
    props: {
        variant: {
            type: Object,
            default: null,
        },
    },
    mounted() {},
    computed: {
        ...mapState("misc", ["warehouseRooms"]),
        
        // Available packages for selection
        packages() {
            return getBatchNos(this.result && this.result.ledgerEntries, this.warehouseId)
        },
        
        // Available packages for a specific row (excluding packages selected in OTHER rows)
        availablePackagesForRow() {
            return (rowIndex) => {
                const selectedInOtherRows = this.packageMoves
                    .map((pm, index) => index !== rowIndex ? pm.packageVal : null)
                    .filter(val => val !== null)
                
                const availableForRow = this.packages.filter(pkg => !selectedInOtherRows.includes(pkg.id))
                
                // Debug logging
                console.log(`Row ${rowIndex}:`, {
                    allPackages: this.packages.length,
                    selectedInOtherRows,
                    availableForRow: availableForRow.length,
                    packageMoves: this.packageMoves
                })
                
                return availableForRow
            }
        },
        
        // Can add more packages if we have unselected packages available
        canAddMorePackages() {
            const allSelectedPackages = this.packageMoves
                .map(pm => pm.packageVal)
                .filter(val => val !== null)
            
            const canAdd = this.packages.length > allSelectedPackages.length
            
            // Debug logging
            console.log('canAddMorePackages:', {
                totalPackages: this.packages.length,
                selectedPackages: allSelectedPackages.length,
                allSelectedPackages,
                canAdd,
                packageMoves: this.packageMoves
            })
            
            return canAdd
        },
        
        // Total units from all packages
        totalUnitsToMove() {
            return this.packageMoves.reduce((total, pm) => {
                const quantity = Number(pm.quantity)
                return total + (Number.isFinite(quantity) ? quantity : 0)
            }, 0)
        },
        
        // Legacy computed properties (keeping for backward compatibility)
        ledgerEntry() {
            if (!this.result) return
            return getLedgerEntry(this.result.ledgerEntries, this.warehouseId, this.packageVal)
        },
        warehouseRoomMaxQty() {
            return getWarehouseRoomMaxQty(
                this.ledgerEntry,
                this.fromWarehouseRoom === this.warehouseRooms.FRONT_ROOM.id
            )
        },
    },
    methods: {
        async fetchStockData() {
            this.loading = true
            try {
                const res = await http.get(
                    `/stock/inventory?variants=true&item_id=${this.variant.id}&return_ledger_entries=true`
                )
                this.warehouseEntries = res.data.result.warehouseEntries

                this.warehouseOptions = this.warehouseEntries.map((item) => ({
                    id: item.warehouse.id,
                    title: item.warehouse.name,
                }))

                this.result = res.data.result
                
                // Reset form after data is loaded to ensure clean state
                this.resetForm()
            } catch (err) {
                console.log("error in fetching stock data", err)
            }
            this.loading = false
        },
        moveInventory() {
            this.showModal = true
        },
        
        // Multi-package management methods
        addPackage() {
            this.packageMoves.push({
                packageVal: null,
                quantity: 0
            })
        },
        
        removePackage(index) {
            if (this.packageMoves.length > 1) {
                this.packageMoves.splice(index, 1)
                // Clear error when removing packages
                this.errorMessage = ""
            }
        },
        
        updatePackage(index, field, value) {
            // Update the package data
            this.$set(this.packageMoves, index, {
                ...this.packageMoves[index],
                [field]: value
            })
            
            // Clear error when user makes changes
            this.errorMessage = ""
            
            // Legacy sync for first package (backward compatibility)
            if (index === 0) {
                if (field === 'packageVal') {
                    this.packageVal = value
                } else if (field === 'quantity') {
                    this.quantity = Number(value) || 0
                }
            }
        },
        
        getPackageMaxQty(packageVal) {
            if (!packageVal || !this.result) return 0
            
            const ledgerEntry = getLedgerEntry(this.result.ledgerEntries, this.warehouseId, packageVal)
            return getWarehouseRoomMaxQty(
                ledgerEntry,
                this.fromWarehouseRoom === this.warehouseRooms.FRONT_ROOM.id
            )
        },
        async moveStock() {
            if (this.movingStock) return
            this.errorMessage = ""
            
            // Validate basic requirements
            if (!this.warehouseId) {
                this.errorMessage = "Please select warehouse location!"
                return
            }
            
            if (!this.fromWarehouseRoom) {
                this.errorMessage = "Please select from room!"
                return
            }
            
            // Validate all packages
            const validPackages = []
            const validationErrors = []
            
            for (let i = 0; i < this.packageMoves.length; i++) {
                const packageMove = this.packageMoves[i]
                const packageNum = i + 1
                
                if (!packageMove.packageVal) {
                    validationErrors.push(`Package ${packageNum}: Please select a package`)
                    continue
                }
                
                if (!Number.isSafeInteger(Number(packageMove.quantity)) || Number(packageMove.quantity) <= 0) {
                    validationErrors.push(`Package ${packageNum}: Please enter a positive whole-unit quantity`)
                    continue
                }
                
                const maxQty = this.getPackageMaxQty(packageMove.packageVal)
                if (packageMove.quantity > maxQty) {
                    validationErrors.push(`Package ${packageNum}: Quantity (${packageMove.quantity}) exceeds available stock (${maxQty})`)
                    continue
                }
                
                // Get ledger entry for this package
                const ledgerEntry = getLedgerEntry(
                    this.result.ledgerEntries, 
                    this.warehouseId, 
                    packageMove.packageVal
                )
                
                if (!ledgerEntry) {
                    validationErrors.push(`Package ${packageNum}: Ledger entry not found`)
                    continue
                }
                
                validPackages.push({
                    ledger_id: ledgerEntry.id,
                    qty: Number(packageMove.quantity)
                })
            }
            
            if (validationErrors.length > 0) {
                this.errorMessage = validationErrors.join('; ')
                return
            }
            
            if (validPackages.length === 0) {
                this.errorMessage = "Please add at least one valid package to move"
                return
            }
            
            this.movingStock = true
            
            try {
                const from_front_room = this.fromWarehouseRoom == this.warehouseRooms.FRONT_ROOM.id

                const res = await http.post("/stock/move", {
                    from_front_room,
                    packages: validPackages
                })
                
                this.$emit("update", res.data.result)
                
                // Enhanced success message
                const direction = from_front_room ? "to back room" : "to front room"
                const packageCount = res.data.packages_moved || validPackages.length
                const totalUnits = res.data.total_units_moved || this.totalUnitsToMove
                
                const successMessage = packageCount === 1 
                    ? `Stock moved successfully ${direction}.`
                    : `${packageCount} packages (${totalUnits} total units) moved successfully ${direction}.`
                
                this.$toast.success(successMessage)
                this.showModal = false
                
                // Reset form
                this.resetForm()
                
            } catch (err) {
                console.log("err in moving stock", err)
                this.$toast.error("Error Occurred")
                
                // Enhanced error handling
                if (err.response && err.response.data && err.response.data.errors) {
                    this.errorMessage = err.response.data.errors.join('; ')
                } else {
                    this.errorMessage = errMsg(err, "Could not move stock")
                }
            }
            
            this.movingStock = false
        },
        
        resetForm() {
            // Reset to single package
            this.packageMoves = [{
                packageVal: null,
                quantity: 0
            }]
            this.packageVal = null
            this.quantity = 0
            this.errorMessage = ""
        },
    },
    watch: {
        showModal(e) {
            console.log("show modal", e, this.result)
            if (e) {
                this.fetchStockData()
                // fetchStockData will reset the form after data loads
            }
        },
        quantity(e) {
            this.errorMessage = ""
            if (e > this.warehouseRoomMaxQty) {
                this.$toast.error("Quantity Exceeded!")
                this.errorMessage = "Max quantity exceeded!"
            }
        },
        
        // Watch packageMoves for validation
        packageMoves: {
            handler(newPackages) {
                // Clear errors when packages change
                this.errorMessage = ""
                
                // Sync first package with legacy fields (only if not null)
                if (newPackages.length > 0 && newPackages[0].packageVal !== null) {
                    this.packageVal = newPackages[0].packageVal
                    this.quantity = newPackages[0].quantity
                }
            },
            deep: true
        },
        fromWarehouseRoom(e) {
            if (e == this.warehouseRooms.BACK_ROOM.id) {
                this.toWarehouseRoom = this.warehouseRooms.FRONT_ROOM.id
            } else if (e) {
                this.toWarehouseRoom = this.warehouseRooms.BACK_ROOM.id
            }
        },
        warehouse() {
            this.resetForm()
        },
        
        warehouseId() {
            this.resetForm()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.inventory-move-modal {
    cursor: pointer;
}

// Multi-package move styles
.package-moves-container {
    .package-move-row {
        margin-bottom: 15px;
        padding: 10px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background-color: #fafafa;
        
        &:first-child {
            border-color: #007bff;
            background-color: #f8f9ff;
        }
    }
    
    .remove-package-btn {
        min-width: 35px;
        height: 35px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #dc3545;
        border-color: #dc3545;
        
        &:hover {
            background-color: #dc3545;
            color: white;
        }
    }
    
    .add-package-section {
        margin-top: 15px;
        text-align: center;
        
        .add-package-btn {
            min-width: 150px;
            color: #28a745;
            border-color: #28a745;
            font-weight: 600;
            
            &:hover {
                background-color: #28a745;
                color: white;
            }
            
            i {
                margin-right: 8px;
            }
        }
    }
}

.unit-to-move {
    font-weight: 600;
    color: #007bff;
    font-size: 16px;
}

.inventory-move-modal-inner {
    position: relative;
    z-index: 1051;
    
    .row {
        margin-bottom: 16px;
    }
}

.unit-to-move {
    font-size: 14px;
    color: $textColor;
    opacity: 0.8;
}

.err-msg {
    color: $dangerColor;
    margin-top: 10px;
    font-size: 14px;
}
</style>
